import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon, X, ScrollText, Receipt as ReceiptIcon, TrendingUp } from "lucide-react";
import { Search } from "@/lib/api";

const ICONS = {
  bill: ScrollText,
  receipt: ReceiptIcon,
  income: TrendingUp,
};

const fmt = (n) =>
  n == null
    ? ""
    : Number(n).toLocaleString("en-AU", {
        style: "currency",
        currency: "AUD",
      });

const CommandPalette = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    let cancel = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const r = await Search.query(q.trim(), 25);
        if (!cancel) setResults(r.results || []);
      } catch {
        if (!cancel) setResults([]);
      } finally {
        if (!cancel) setBusy(false);
      }
    }, 180);
    return () => {
      cancel = true;
      clearTimeout(t);
    };
  }, [q]);

  // Keyboard shortcuts inside the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((a) => Math.min(results.length - 1, a + 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((a) => Math.max(0, a - 1));
      }
      if (e.key === "Enter" && results[active]) {
        e.preventDefault();
        go(results[active]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, results, active]);

  const go = (item) => {
    onClose?.();
    if (item?.url) navigate(item.url);
  };

  if (!open) return null;

  return (
    <div
      data-testid="command-palette"
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center px-4 pt-[10vh]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white border-2 border-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b-2 border-black px-4 py-3">
          <SearchIcon className="h-5 w-5" />
          <input
            ref={inputRef}
            data-testid="palette-input"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Search bills, receipts, customers, jobs…"
            className="flex-1 bg-transparent outline-none text-base placeholder:text-neutral-400"
          />
          <kbd className="hidden sm:inline-block text-[10px] uppercase tracking-widest border border-neutral-300 px-2 py-1 font-bold">
            ESC
          </kbd>
          <button
            onClick={onClose}
            className="text-neutral-500 hover:text-black"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="palette-results">
          {q.trim() && results.length === 0 && !busy && (
            <div className="px-5 py-8 text-sm text-neutral-500">
              No matches. Try a company name, invoice number, vendor or tag.
            </div>
          )}
          {!q.trim() && (
            <div className="px-5 py-6 text-sm text-neutral-500">
              <div className="font-bold uppercase tracking-widest text-[10px] text-neutral-400 mb-2">
                Quick tips
              </div>
              <ul className="space-y-1">
                <li>• Type a company, vendor, invoice #, or customer.</li>
                <li>• Search by tag/job ID (e.g. <code>#smith</code>).</li>
                <li>• Press <kbd className="border border-neutral-300 px-1 text-[10px]">↑</kbd>/<kbd className="border border-neutral-300 px-1 text-[10px]">↓</kbd> to navigate, <kbd className="border border-neutral-300 px-1 text-[10px]">Enter</kbd> to open.</li>
              </ul>
            </div>
          )}
          {results.map((r, i) => {
            const Icon = ICONS[r.type] || ScrollText;
            return (
              <button
                key={`${r.type}-${r.id}`}
                data-testid={`result-${i}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(r)}
                className={`w-full text-left px-5 py-3 border-b border-neutral-100 flex items-center gap-4 ${
                  i === active ? "bg-neutral-100" : "bg-white"
                }`}
              >
                <Icon className="h-4 w-4 flex-none" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold truncate">{r.title}</div>
                  <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 truncate">
                    {r.type} · {r.subtitle}
                    {r.tags?.length > 0 && (
                      <span className="ml-2 text-neutral-700">
                        {r.tags.map((t) => `#${t}`).join(" ")}
                      </span>
                    )}
                  </div>
                </div>
                {r.amount != null && (
                  <div className="text-right">
                    <div className="font-display font-black mono">
                      {fmt(r.amount)}
                    </div>
                    {r.date && (
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-0.5">
                        {r.date}
                      </div>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
